// ============================================
// pages/cao/CaoDashboard.tsx
// Chief Accounts Officer releases payments against manager-approved rows.
// Each row can take many partial payments until balance hits zero.
// ============================================
import React, { useEffect, useState, useCallback } from 'react';
import { AlertCircle, CheckCircle, Wallet, Plus, Trash2, Eye } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import {
  reportsAPI, gunnyUtilizationPaymentsAPI, seasonsAPI, GunnyProformaRow,
} from '../../api/services';
import { GunnyStepTracker } from '../../components/gunny/GunnyStepTracker';
import { GunnyRowDetail } from '../../components/gunny/GunnyRowDetail';
import { GunnyFilterBar, applyFilters, emptyFilter, GunnyFilterState } from '../../components/gunny/GunnyFilterBar';

interface PaymentRow {
  id: number;
  paid_on: string;
  amount_rs: number | string;
  mode: string;
  utr_no: string | null;
  bank_name: string | null;
}

interface PayState {
  utilizationId: number;
  district: string;
  merchant: string;
  society: string | null;
  mp_gross: number;
  already_paid: number;
  balance: number;
  paid_on: string;
  amount_rs: number;
  mode: string;
  utr_no: string;
  bank_name: string;
  existing: PaymentRow[];
}

const MODES = ['neft', 'rtgs', 'imps', 'upi', 'cheque', 'cash', 'other'];

export const CaoDashboard: React.FC = () => {
  const { user } = useAuth();
  const [rows, setRows] = useState<GunnyProformaRow[]>([]);
  const [seasonId, setSeasonId] = useState<number | null>(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);
  const [flash, setFlash] = useState<string | null>(null);
  const [pay, setPay] = useState<PayState | null>(null);
  const [detail, setDetail] = useState<GunnyProformaRow | null>(null);
  const [saving, setSaving] = useState(false);
  const [statusFilter, setStatusFilter] = useState<'due' | 'all'>('due');
  const [filters, setFilters] = useState<GunnyFilterState>(emptyFilter);

  const load = useCallback(async (sid: number) => {
    setLoading(true);
    setErr(null);
    try {
      const { data } = await reportsAPI.gunniesProforma({ season_id: sid });
      setRows(data.rows);
    } catch (e: any) {
      setErr(e?.response?.data?.message || 'Failed to load proforma');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await seasonsAPI.active();
        if (!data?.id) { setErr('No active season configured'); setLoading(false); return; }
        setSeasonId(data.id);
        load(data.id);
      } catch (e: any) {
        setErr(e?.response?.data?.message || 'Failed to load the active season');
        setLoading(false);
      }
    })();
  }, [load]);

  const baseVisible = statusFilter === 'due'
    ? rows.filter((r) => r.status === 'manager_approved' && r.balance_to_release > 0)
    : rows;
  const visible = applyFilters(baseVisible, filters);

  const openPay = async (r: GunnyProformaRow) => {
    try {
      const { data } = await gunnyUtilizationPaymentsAPI.list(r.id);
      setPay({
        utilizationId: r.id,
        district: r.district,
        merchant: r.merchant,
        society: r.society_name,
        mp_gross: Number(r.mp_gross_amount || r.gross_amount || 0),
        already_paid: Number(r.payment_released || 0),
        balance: Number(r.balance_to_release || 0),
        paid_on: new Date().toISOString().slice(0, 10),
        amount_rs: Number(r.balance_to_release || 0),
        mode: 'rtgs',
        utr_no: '',
        bank_name: '',
        existing: data || [],
      });
    } catch (e: any) {
      setErr(e?.response?.data?.message || 'Could not load existing payments');
    }
  };

  const submit = async () => {
    if (!pay) return;
    if (pay.amount_rs <= 0) { setErr('Amount must be greater than zero'); return; }
    if (pay.amount_rs > pay.balance) { setErr(`Amount exceeds balance (₹${pay.balance.toFixed(2)})`); return; }

    setSaving(true);
    setErr(null);
    try {
      await gunnyUtilizationPaymentsAPI.create(pay.utilizationId, {
        paid_on: pay.paid_on,
        amount_rs: pay.amount_rs,
        mode: pay.mode,
        utr_no: pay.utr_no || undefined,
        bank_name: pay.bank_name || undefined,
      });
      setFlash(`Recorded ₹${pay.amount_rs.toLocaleString('en-IN')} against utilization #${pay.utilizationId}`);
      setPay(null);
      if (seasonId) await load(seasonId);
      setTimeout(() => setFlash(null), 2500);
    } catch (e: any) {
      setErr(e?.response?.data?.message || 'Payment record failed');
    } finally {
      setSaving(false);
    }
  };

  const deletePayment = async (payId: number) => {
    if (!pay) return;
    if (!window.confirm('Delete this payment record?')) return;
    try {
      await gunnyUtilizationPaymentsAPI.remove(payId);
      // reload payments + main list
      const { data } = await gunnyUtilizationPaymentsAPI.list(pay.utilizationId);
      const newSum = (data || []).reduce((s: number, p: any) => s + Number(p.amount_rs || 0), 0);
      setPay({ ...pay, existing: data || [], already_paid: newSum, balance: Math.max(pay.mp_gross - newSum, 0) });
      if (seasonId) await load(seasonId);
    } catch (e: any) {
      setErr(e?.response?.data?.message || 'Delete failed');
    }
  };

  const fmt = (v: number | null) =>
    v == null ? '—' : Number(v).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

  return (
    <div>
      <div className="flex items-start justify-between mb-4">
        <div>
          <h1 className="text-xl font-bold text-gray-900">CAO — Payment Release</h1>
          <p className="text-sm text-gray-600">
            Release payments against gunny-bag bills that Manager Procurement has approved. Partial payments are
            supported — add as many as needed until the balance is cleared.
          </p>
        </div>
        <div className="flex gap-1 bg-white border rounded p-1 text-xs">
          <button
            onClick={() => setStatusFilter('due')}
            className={`px-3 py-1 rounded ${statusFilter === 'due' ? 'bg-rose-600 text-white' : 'text-gray-600'}`}
          >
            Due ({rows.filter((r) => r.status === 'manager_approved' && r.balance_to_release > 0).length})
          </button>
          <button
            onClick={() => setStatusFilter('all')}
            className={`px-3 py-1 rounded ${statusFilter === 'all' ? 'bg-rose-600 text-white' : 'text-gray-600'}`}
          >
            All ({rows.length})
          </button>
        </div>
      </div>

      <GunnyFilterBar rows={rows} value={filters} onChange={setFilters} />

      {err && (
        <div className="mb-3 flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded text-red-700 text-sm">
          <AlertCircle className="w-4 h-4" /> {err}
        </div>
      )}
      {flash && (
        <div className="mb-3 flex items-center gap-2 p-3 bg-green-50 border border-green-200 rounded text-green-700 text-sm">
          <CheckCircle className="w-4 h-4" /> {flash}
        </div>
      )}

      <div className="bg-white rounded-xl shadow border overflow-hidden">
        {loading ? (
          <div className="p-10 text-center text-gray-500">Loading…</div>
        ) : visible.length === 0 ? (
          <div className="p-10 text-center text-gray-500">
            {statusFilter === 'due' ? 'No payments pending.' : 'No rows in this season.'}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 border-b">
                <tr className="text-left text-xs uppercase text-gray-600">
                  <th className="px-3 py-2">#</th>
                  <th className="px-3 py-2">District</th>
                  <th className="px-3 py-2">Supplier</th>
                  <th className="px-3 py-2">Society</th>
                  <th className="px-3 py-2 text-right">Approved Gross</th>
                  <th className="px-3 py-2 text-right">Already Paid</th>
                  <th className="px-3 py-2 text-right">Balance</th>
                  <th className="px-3 py-2">Last Paid</th>
                  <th className="px-3 py-2">Status</th>
                  <th className="px-3 py-2 text-right">Action</th>
                </tr>
              </thead>
              <tbody>
                {visible.map((r) => (
                  <tr key={r.id} className="border-b last:border-0 hover:bg-gray-50">
                    <td className="px-3 py-2 text-gray-500">{r.sl_no}</td>
                    <td className="px-3 py-2">{r.district}</td>
                    <td className="px-3 py-2 truncate max-w-[220px]">{r.merchant}</td>
                    <td className="px-3 py-2">{r.society_name || '—'}</td>
                    <td className="px-3 py-2 text-right tabular-nums">{fmt(r.mp_gross_amount || r.gross_amount)}</td>
                    <td className="px-3 py-2 text-right tabular-nums">{fmt(r.payment_released)}</td>
                    <td className="px-3 py-2 text-right tabular-nums font-semibold">{fmt(r.balance_to_release)}</td>
                    <td className="px-3 py-2 text-xs text-gray-600">{r.last_paid_on || '—'}</td>
                    <td className="px-3 py-2"><GunnyStepTracker row={r} /></td>
                    <td className="px-3 py-2 text-right whitespace-nowrap">
                      <button
                        onClick={() => setDetail(r)}
                        className="inline-flex items-center gap-1 px-2 py-1 text-xs bg-white border border-gray-300 hover:bg-gray-50 text-gray-700 rounded mr-1"
                      >
                        <Eye className="w-3 h-3" /> View
                      </button>
                      {r.status === 'manager_approved' && r.balance_to_release > 0 ? (
                        <button
                          onClick={() => openPay(r)}
                          className="inline-flex items-center gap-1 px-3 py-1 text-xs bg-rose-600 hover:bg-rose-700 text-white rounded"
                        >
                          <Wallet className="w-3 h-3" /> Pay
                        </button>
                      ) : (
                        <span className="text-xs text-gray-400">
                          {r.balance_to_release === 0 && r.payment_released > 0 ? 'paid' : r.status}
                        </span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <div className="mt-3 text-xs text-gray-500">Signed in as <span className="font-medium">{user?.name}</span>.</div>

      {pay && (
        <PayModal state={pay} setState={setPay} submit={submit} saving={saving} onClose={() => setPay(null)} deletePayment={deletePayment} />
      )}

      <GunnyRowDetail
        row={detail}
        onClose={() => setDetail(null)}
        onRowUpdated={() => { if (seasonId) load(seasonId); }}
      />
    </div>
  );
};

const PayModal: React.FC<{
  state: PayState;
  setState: (s: PayState) => void;
  submit: () => void;
  saving: boolean;
  onClose: () => void;
  deletePayment: (id: number) => void;
}> = ({ state, setState, submit, saving, onClose, deletePayment }) => {
  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl">
        <div className="px-6 py-4 border-b">
          <h2 className="font-semibold text-gray-900">Record payment</h2>
          <p className="text-xs text-gray-500 mt-0.5">
            {state.district} · {state.merchant} · {state.society || '—'}
          </p>
        </div>

        <div className="px-6 py-4 bg-rose-50 border-b">
          <div className="grid grid-cols-3 gap-3 text-sm">
            <div>
              <p className="text-xs text-gray-500">Approved gross</p>
              <p className="font-semibold tabular-nums">₹ {state.mp_gross.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</p>
            </div>
            <div>
              <p className="text-xs text-gray-500">Already paid</p>
              <p className="font-semibold tabular-nums">₹ {state.already_paid.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</p>
            </div>
            <div>
              <p className="text-xs text-gray-500">Balance</p>
              <p className="font-semibold text-rose-700 tabular-nums">₹ {state.balance.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</p>
            </div>
          </div>
        </div>

        {state.existing.length > 0 && (
          <div className="px-6 py-3 border-b">
            <p className="text-xs font-semibold text-gray-600 mb-2">Previous payments</p>
            <div className="space-y-1">
              {state.existing.map((p) => (
                <div key={p.id} className="flex items-center justify-between text-xs bg-gray-50 rounded px-2 py-1">
                  <span>
                    {p.paid_on} · <span className="uppercase">{p.mode}</span>{' '}
                    {p.utr_no && <span className="text-gray-500">{p.utr_no}</span>}
                  </span>
                  <div className="flex items-center gap-2">
                    <span className="tabular-nums">₹ {Number(p.amount_rs).toLocaleString('en-IN', { minimumFractionDigits: 2 })}</span>
                    <button onClick={() => deletePayment(p.id)} className="text-red-500 hover:text-red-700"><Trash2 className="w-3 h-3" /></button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        <div className="px-6 py-5 grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Payment date</label>
            <input type="date" value={state.paid_on}
              onChange={(e) => setState({ ...state, paid_on: e.target.value })}
              className="w-full px-3 py-2 border rounded text-sm" />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Amount</label>
            <input type="number" min={0} step="0.01" max={state.balance}
              value={state.amount_rs}
              onChange={(e) => setState({ ...state, amount_rs: Number(e.target.value) || 0 })}
              className="w-full px-3 py-2 border rounded text-sm" />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Mode</label>
            <select value={state.mode}
              onChange={(e) => setState({ ...state, mode: e.target.value })}
              className="w-full px-3 py-2 border rounded text-sm bg-white">
              {MODES.map((m) => <option key={m} value={m}>{m.toUpperCase()}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">UTR No.</label>
            <input type="text" value={state.utr_no}
              onChange={(e) => setState({ ...state, utr_no: e.target.value })}
              className="w-full px-3 py-2 border rounded text-sm" />
          </div>
          <div className="col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-1">Bank</label>
            <input type="text" value={state.bank_name}
              onChange={(e) => setState({ ...state, bank_name: e.target.value })}
              className="w-full px-3 py-2 border rounded text-sm" />
          </div>
        </div>

        <div className="px-6 py-4 border-t flex justify-end gap-2 bg-gray-50">
          <button onClick={onClose} className="px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 rounded">Cancel</button>
          <button
            onClick={submit}
            disabled={saving || state.amount_rs <= 0 || state.amount_rs > state.balance}
            className="inline-flex items-center gap-1 px-4 py-2 text-sm bg-rose-600 hover:bg-rose-700 text-white rounded disabled:opacity-50"
          >
            <Plus className="w-4 h-4" /> {saving ? 'Recording…' : 'Record payment'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default CaoDashboard;
